import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { App } from './App';
import { AUTH_QUERY_KEY } from './api/auth';
import { onSessionLost } from './platform/session';
import { initScrollBehavior } from './router/scroll';
import { initSettingsSync } from './platform/settingsSync';
import { initTokenRenewal } from './platform/tokenRenewal';
import { initUiLocale } from './strings/boot';
import { registerServiceWorker } from './platform/serviceWorker';
import { initTheme } from './platform/theme';
/*
 * Order matters and is the only reason these are two imports: the generated custom properties have to
 * be defined before the base layer reads them. Every *other* stylesheet in the app is a CSS Module
 * imported by the component that uses it — `styles/global.css` is the last global one.
 */
import '@visious/tokens/css/tokens.css';
import './styles/global.css';

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element #root not found');
}

/*
 * Before `createRoot`, not in an effect: React renders after this line, so a light-theme reader never
 * sees a dark first frame. The same stamp from inside a component would land one paint late — which
 * is exactly the flash §9's "both themes are token swaps" is supposed to make impossible.
 */
initTheme();

/*
 * This is here for `initTheme`'s reason rather than by analogy with it: the first render reads
 * the catalogue, so resolving the language *after* `createRoot` would paint one frame of the fallback
 * and then swap every word on the page. Both are synchronous reads of `localStorage` plus a browser
 * preference, so neither costs a round trip.
 */
initUiLocale();

/*
 * §10 Phase 4's offline shell (4d). After the theme and before React, but it does nothing until the
 * `load` event — see the file. Registering it here rather than from a component keeps it out of every
 * screen's dependency graph: it is a property of the *document*, not of anything rendered in it.
 */
registerServiceWorker();

/**
 * §4 puts TanStack Query in the app layer and never in core. The defaults below are the ones that
 * matter, and both of them defer to core:
 *
 * - `retry: false` — §3's retry and backoff live in `createApiTransport`. A second retry loop on
 *   top would multiply requests against an instance that is already failing.
 * - `refetchOnWindowFocus: false` — a signed media URL is good for ~5h45m and the manifest rotates
 *   it well inside that (0f). Re-fetching a video every time the tab regains focus buys nothing and
 *   costs 305 KB a time (§8's hydration measurement).
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

/*
 * When the session goes, so does everything it bought. Here rather than in the sign-out
 * button, because a sign-out in *another tab* reaches this tab through the session store and not through
 * any handler — and `AUTH_QUERY_KEY` is a prefix rather than `clear()` so Home, Trending and every
 * channel page survive it.
 */
onSessionLost(() => {
  queryClient.removeQueries({ queryKey: [AUTH_QUERY_KEY] });
});

/*
 * The other thing a session buys: while one is live, the account's preferences are the settings and
 * the local stores are their cache (`platform/settingsSync.ts`). Initialised here rather than from
 * the settings page, because the pull has to happen on every signed-in boot — whichever page it
 * lands on — and the module also clears its own stamp when the session goes.
 */
initSettingsSync();

/*
 * Silent token renewal (2026-08-02): a session granted the opt-in renewal scope mints its own
 * successor inside its last week (`platform/tokenRenewal.ts`). Here for `initSettingsSync`'s reason
 * — it must run on every signed-in boot, whichever page it lands on.
 */
initTokenRenewal();

/*
 * Scroll behaviour (2026-08-02): screen switches read from the top, Back/Forward restore the
 * entry's position. Before React for `initTheme`'s reason in reverse — a reload's restore should be
 * racing the first render, not queued behind it.
 */
initScrollBehavior();

createRoot(rootElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </StrictMode>,
);
